<script setup lang="ts">
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { n } from "components/jiffy/vue-cart/helpers/currency";
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { api } from "jiffy-api";
import { useDtfStore } from "../../store";

const props = defineProps<{
  disabled: boolean;
  onLearnMore?: () => void;
  index?: number;
}>();

const store = useDtfStore();
const isRedrawDelayTextVisible = computed(() => store.isRedrawDelayTextVisible);
const isFaceliftExperimentActive = computed(() => store.isFaceliftExperimentActive);

const placeholderText = computed(() => {
  if (isFaceliftExperimentActive.value) {
    return "i.e. Make the font black, remove the red background, and smooth out edges.";
  }
  return 'Example: "Change the color of the font to black. Remove the background from the top half of the image."';
});

const artwork = computed(() => {
  if (props.index === undefined) return null;
  return store.artworks[props.index];
});

const shouldShowPrice = computed(() => {
  if (!artwork.value?.pricing) return false;
  const price = (artwork.value.pricing as any).expertEditAddonTierPrice;
  return Number(price) > 0;
});

const formattedPrice = computed(() => {
  if (!artwork.value?.pricing) return "";
  const price = (artwork.value.pricing as any).expertEditAddonTierPrice;
  return n(price, "currency");
});
const emit = defineEmits(["input"]);
const model = defineModel<string>({ default: "" });
const textareaRef = ref<HTMLTextAreaElement | null>(null);
const isSavingVisible = ref(false);
const isSavedVisible = ref(false);
let savingTimeoutId: number | null = null;

function autoResize(event: Event) {
  const textarea = event.target as HTMLTextAreaElement;
  if (textarea && isFaceliftExperimentActive.value) {
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }
}

function handleSavingIndicator() {
  isSavingVisible.value = true;
  isSavedVisible.value = false;
  if (savingTimeoutId !== null) {
    clearTimeout(savingTimeoutId);
  }
  savingTimeoutId = window.setTimeout(() => {
    saveRedrawNote();
  }, 1000);
}

function saveRedrawNote() {
  if (!artwork.value?.id) return;
  const note = model.value.trim();
  api.dtf
    .updateArtworkRedrawData(
      { id: artwork.value.id },
      { dtf_artwork: { enhancement_request_note: note } }
    )
    .then(() => {
      isSavingVisible.value = false;
      isSavedVisible.value = true;
    })
    .catch(() => {
      isSavingVisible.value = false;
      isSavedVisible.value = false;
    });
}

watch(model, () => {
  if (isFaceliftExperimentActive.value && textareaRef.value) {
    nextTick(() => {
      if (textareaRef.value) {
        textareaRef.value.style.height = "auto";
        textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`;
      }
    });
  }
});

onBeforeUnmount(() => {
  if (savingTimeoutId !== null) {
    clearTimeout(savingTimeoutId);
  }
});
</script>

<template>
  <div class="custom-notes">
    <label class="custom-notes__label">
      <span class="custom-notes__label-text">
        <span v-if="isFaceliftExperimentActive" class="custom-notes__label-with-badge">
          <span class="custom-notes__label-text-with-icon">
            Need artist edits?
            <button
              v-if="props.onLearnMore"
              class="custom-notes__learn-more-icon"
              type="button"
              @click="props.onLearnMore"
            >
              <UseSvg icon="question-circle" class="custom-notes__question-icon" />
            </button>
          </span>
          <span v-if="isSavingVisible" class="custom-notes__saving-status">
            <UseSvg icon="rotate" class="custom-notes__saving-icon" />
            Saving...
          </span>
          <span
            v-else-if="isSavedVisible"
            class="custom-notes__saving-status custom-notes__saving-status--saved"
          >
            <UseSvg icon="check" class="custom-notes__saved-icon" />
            Saved
          </span>
          <span v-if="!shouldShowPrice" class="custom-notes__free-badge">Free</span>
          <span v-else class="custom-notes__price-badge">{{ formattedPrice }}</span>
        </span>
        <template v-else>
          <span class="custom-notes__label-required">*</span>
          <span>Instructions for our designers</span>
          <span v-if="isSavingVisible" class="custom-notes__saving-status">
            <UseSvg icon="rotate" class="custom-notes__saving-icon" />
            Saving...
          </span>
          <span
            v-else-if="isSavedVisible"
            class="custom-notes__saving-status custom-notes__saving-status--saved"
          >
            <UseSvg icon="check" class="custom-notes__saved-icon" />
            Saved
          </span>
        </template>
      </span>
      <span v-if="isRedrawDelayTextVisible" class="custom-notes__note">
        Due to higher than normal volume, requesting design edits may delay your delivery date by 1
        day.
      </span>
      <div v-if="isFaceliftExperimentActive" class="custom-notes__textarea-wrapper">
        <textarea
          ref="textareaRef"
          v-model="model"
          :disabled="disabled"
          class="custom-notes__textarea custom-notes__textarea--experiment js-custom-notes-textarea"
          maxlength="500"
          :placeholder="placeholderText"
          @input="
            autoResize($event);
            emit('input', $event);
            handleSavingIndicator();
          "
        ></textarea>
      </div>
      <textarea
        v-else
        v-model="model"
        :disabled="disabled"
        class="custom-notes__textarea js-custom-notes-textarea"
        maxlength="500"
        :placeholder="placeholderText"
        @input="
          emit('input', $event);
          handleSavingIndicator();
        "
      ></textarea>
    </label>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.custom-notes {
  &__label {
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: $mirage;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 16px;
  }

  &__label-text {
    padding-right: 0;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__label-with-badge {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__label-text-with-icon {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }

  &__free-badge {
    background-color: $green-discount-text;
    color: $white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.3;
    white-space: nowrap;
    text-transform: uppercase;
  }

  &__price-badge {
    color: $price-product-card;
    padding: 4px 12px;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.3;
    white-space: nowrap;
  }

  &__note {
    font-size: 11px;
    font-weight: normal;

    &--delivery {
      font-size: 12px;
    }
  }

  &__label-required {
    color: $red-pure;
  }

  &__textarea-wrapper {
    background: conic-gradient(
      from 136.6deg at 48.4% 54.54%,
      #354dff 0deg,
      #b144fe 109.06deg,
      #4f4bfd 182.47deg,
      #9d4afc 287.94deg,
      #354dff 360deg
    );
    border-radius: 8px;
    padding: 2px;
    position: relative;
    line-height: 0;

    &:focus-within {
      box-shadow: 0 0 0 3px rgba(53, 77, 255, 0.1);
    }
  }

  &__textarea {
    border-radius: 4px;
    padding: 8px;
    width: 100%;
    height: 100px;
    box-sizing: border-box;
    resize: none;
    border: 1px solid $crafterpay-gray-modern-400;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;

    &:disabled {
      opacity: 0.5;
    }

    &--experiment {
      border: none;
      border-radius: 6px;
      padding: 5px 12px;
      background-color: #f9fafb;
      font-size: 14px;
      line-height: 18px;
      min-height: 100px;
      height: auto;
      resize: vertical;
      overflow-y: hidden;

      &:focus {
        outline: none;
        background-color: $white;
      }
    }
  }

  &__saving-status {
    border-radius: 20px;
    padding: 4px 8px;
    background-color: #e5e7eb;
    margin-right: auto;
  }

  &__saving-status--saved {
    background-color: transparent;
    border: 1.5px solid #e5e7eb;
  }

  &__saved-icon {
    width: 14px;
    height: 14px;
    margin-right: 4px;
  }

  &__saving-icon {
    width: 14px;
    height: 14px;
    margin-right: 4px;
    fill: none;
    animation: custom-notes-rotate 1s linear infinite;
  }
}

@keyframes custom-notes-rotate {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(-360deg);
  }
}

.custom-notes {
  &__learn-more-icon {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    margin: 0 4px;

    &:hover {
      transform: scale(1.1);
    }

    &:focus-visible {
      outline: 2px solid $blue;
      outline-offset: 2px;
      border-radius: 50%;
    }
  }

  &__question-icon {
    width: 16px;
    height: 16px;
    color: $blue;
  }

  &__notes-text {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
}

.design-services {
  &__learn-more {
    background: none;
    border: none;
    color: $blue;
    text-decoration: underline;
    cursor: pointer;
    font-size: 14px;
    padding: 0;
    line-height: 1;
    transition: color 0.2s ease;

    &:hover {
      color: darken($blue, 10%);
    }

    &:focus-visible {
      outline: 2px solid $blue;
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
}
</style>
